import React from 'react';
import { isWorkEmail, workEmailError } from '../../utils/workEmail';
import { nameFromEmail } from './peopleModel';
import styles from './css/index.module.css';

const emptyPerson = () => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, email: '', groupId: '', admin: false });

function personError(person, people) {
    const email = person.email.trim();
    if (!email) return 'Email is required.';
    const emailError = workEmailError(email);
    if (emailError) return emailError;
    if (!isWorkEmail(email)) return 'Use a company email address.';
    if (!person.admin && !person.groupId) return 'Access group is required.';
    const duplicate = people.filter(item => item.email.trim().toLowerCase() === email.toLowerCase()).length > 1;
    if (duplicate) return 'This email is already in the list.';
    return '';
}

export default function AddMembersDrawer({ groups, saving, onClose, onSend }) {
    const [step, setStep] = React.useState('edit');
    const [people, setPeople] = React.useState([emptyPerson()]);

    const started = person => Boolean(person.email.trim() || person.groupId || person.admin);
    const activePeople = people.filter(started);
    const errors = people.map(person => (started(person) ? personError(person, people.filter(started)) : ''));
    const ready = activePeople.length > 0 && activePeople.every(person => !personError(person, activePeople));

    const update = (id, patch) => {
        setPeople(prev => prev.map(person => (person.id === id ? { ...person, ...patch } : person)));
    };

    const adminCount = people.filter(person => person.admin).length;

    return (
        <>
            <div className={styles.backdrop} onClick={onClose} />
            <aside className={styles.drawer} role="dialog" aria-label={step === 'edit' ? 'Add team members' : 'Review invitations'}>
                <div className={styles.drawerHead}>
                    <div>
                        <h2 className={styles.title}>{step === 'edit' ? 'Add team members' : 'Review invitations'}</h2>
                        <p className={styles.subtitle}>
                            {step === 'edit' ? 'Invite one or several people to this project.' : 'Confirm access groups and administrator status before sending.'}
                        </p>
                    </div>
                    <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>
                <div className={styles.drawerBody}>
                    {step === 'edit'
                        ? people.map((person, index) => {
                              return (
                                  <div key={person.id} className={styles.inviteCard}>
                                      <div className={styles.inviteCardHead}>
                                          <span>Person {index + 1}</span>
                                          {people.length > 1 ? (
                                              <button type="button" className={styles.inviteRemove} onClick={() => setPeople(prev => prev.filter(item => item.id !== person.id))}>
                                                  Remove
                                              </button>
                                          ) : null}
                                      </div>
                                      <div className={styles.inviteFields}>
                                          <label className={styles.inviteField}>
                                              <span>
                                                  Email <span className={styles.req}>*</span>
                                              </span>
                                              <input
                                                  className={styles.input}
                                                  value={person.email}
                                                  placeholder="name@company.com"
                                                  onChange={event => update(person.id, { email: event.target.value })}
                                              />
                                          </label>
                                          <label className={`${styles.inviteField} ${person.admin ? styles.inviteFieldDisabled : ''}`}>
                                              <span>
                                                  Access group {person.admin ? null : <span className={styles.req}>*</span>}
                                              </span>
                                              <select
                                                  className={styles.select}
                                                  value={person.admin ? '' : person.groupId}
                                                  disabled={person.admin}
                                                  onChange={event => update(person.id, { groupId: event.target.value })}>
                                                  <option value="">Select access group</option>
                                                  {groups.map(item => (
                                                      <option key={item.id} value={item.id}>
                                                          {item.name}
                                                      </option>
                                                  ))}
                                              </select>
                                          </label>
                                      </div>
                                      <button
                                          type="button"
                                          className={styles.adminToggle}
                                          onClick={() => update(person.id, person.admin ? { admin: false } : { admin: true, groupId: '' })}>
                                          <span className={`${styles.box} ${person.admin ? styles.boxOn : ''}`}>{person.admin ? <span className={styles.tick} /> : null}</span>
                                          <span className={styles.adminCopy}>
                                              <span className={styles.adminTitle}>Make this user a Project Administrator</span>
                                              <span className={styles.adminHint}>
                                                  Project Administrators can manage this project, its team and access settings using the existing Lens administrator permissions.
                                              </span>
                                          </span>
                                      </button>
                                      {person.admin ? <span className={styles.adminPill}>Project Administrator</span> : null}
                                      {errors[index] ? <p className={styles.error}>{errors[index]}</p> : null}
                                  </div>
                              );
                          })
                        : people.map(person => {
                              const group = groups.find(item => String(item.id) === String(person.groupId));
                              return (
                                  <div key={person.id} className={styles.summary}>
                                      <div className={styles.cardHead}>
                                          <strong>{nameFromEmail(person.email)}</strong>
                                          {person.admin ? <span className={styles.pill}>Project Administrator</span> : null}
                                      </div>
                                      <p className={styles.personEmail}>{person.email}</p>
                                      <p className={styles.personGroup}>Access Group: {person.admin ? 'Administrator' : group?.name}</p>
                                  </div>
                              );
                          })}
                    {step === 'edit' ? (
                        <button type="button" className={styles.textBtn} onClick={() => setPeople(prev => [...prev, emptyPerson()])}>
                            + Add another person
                        </button>
                    ) : (
                        <div className={styles.summaryBox}>
                            <strong>
                                {people.length} {people.length === 1 ? 'person' : 'people'} will be invited.
                            </strong>
                            <p className={styles.hint}>{adminCount === 1 ? '1 will be added as a Project Administrator.' : `${adminCount} will be added as Project Administrators.`}</p>
                        </div>
                    )}
                </div>
                <div className={styles.drawerFoot}>
                    {step === 'edit' ? (
                        <>
                            <button type="button" className={styles.ghostBtn} onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.primaryBtn}
                                disabled={!ready}
                                onClick={() => {
                                    setPeople(activePeople);
                                    setStep('review');
                                }}>
                                Review invitations
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" className={styles.ghostBtn} onClick={() => setStep('edit')}>
                                Back
                            </button>
                            <button type="button" className={styles.primaryBtn} disabled={saving || !ready} onClick={() => onSend(people)}>
                                {saving ? 'Sending…' : 'Send invitations'}
                            </button>
                        </>
                    )}
                </div>
            </aside>
        </>
    );
}
